import { useState } from "react";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { useI18n } from "@/hooks/useI18n";
import { useFullProfile } from "@/hooks/useFullProfile";
import { toast } from "@/hooks/useToast";
import { createStripePaymentLink } from "@/lib/itineraries/stripe-link";

interface UseStripePaymentLinkOptions {
  onStateChange?: (isGenerating: boolean, isSuccess: boolean) => void;
}

export const useStripePaymentLink = (
  itinerary: DashboardItinerary,
  userRole?: string,
  options?: UseStripePaymentLinkOptions
) => {
  const { t } = useI18n();
  const { profileData } = useFullProfile();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const generate = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    setIsSuccess(false);
    options?.onStateChange?.(true, false);
    try {
      const advisor = itinerary.assignedAdvisor;
      const isMember = userRole === 'travel_advisor_member';

      // 1. Stripe risolto dalla view DB: per travel_advisor_member usa già l'agency Stripe via COALESCE
      let stripeAccountId: string | null = null;
      if (advisor?.stripeAccountId && advisor.stripeChargesEnabled) {
        stripeAccountId = advisor.stripeAccountId;
      }

      // 2. Fallback al Stripe del current user (per agency, standalone TD, admin)
      //    Un membro non deve usare il proprio Stripe personale come fallback
      if (!stripeAccountId && !isMember) {
        const myPrivate = (profileData as any)?.private_data;
        if (myPrivate?.stripe_account_id && myPrivate?.stripe_charges_enabled) {
          stripeAccountId = myPrivate.stripe_account_id;
        }
      }

      if (!stripeAccountId) {
        // Determina se l'account esiste ma non è ancora attivo (pending/restricted)
        const advisorStatus = advisor?.stripeAccountStatus;
        const myPrivate = (profileData as any)?.private_data;
        const myStatus = myPrivate?.stripe_account_status;
        const effectiveStatus = isMember ? advisorStatus : (advisorStatus ?? myStatus);
        const isPendingOrRestricted = effectiveStatus === 'pending' || effectiveStatus === 'restricted';

        options?.onStateChange?.(false, false);

        console.warn('[useStripePaymentLink] Stripe non disponibile — debug info:', {
          userRole,
          isMember,
          advisorId: advisor?.id,
          advisorStripeAccountId: advisor?.stripeAccountId,
          advisorStripeChargesEnabled: advisor?.stripeChargesEnabled,
          advisorStripeAccountStatus: advisor?.stripeAccountStatus,
          profileDataLoaded: profileData !== undefined,
          myStripeAccountId: myPrivate?.stripe_account_id,
          myStripeChargesEnabled: myPrivate?.stripe_charges_enabled,
          myStripeAccountStatus: myStatus,
          effectiveStatus,
          isPendingOrRestricted,
        });

        if (isMember) {
          toast({
            title: t("itinerary.agencyStripeNotConfigured"),
            description: isPendingOrRestricted
              ? t("itinerary.agencyStripeAccountNotReady")
              : t("itinerary.agencyStripeNotConfiguredDesc"),
            variant: "destructive",
          });
        } else {
          toast({
            title: t("itinerary.stripeNotConfigured"),
            description: isPendingOrRestricted
              ? t("itinerary.stripeAccountNotReady")
              : t("itinerary.stripeNotConfiguredDesc"),
            variant: "destructive",
          });
        }
        return;
      }

      const paymentLink = await createStripePaymentLink({
        itineraryId: itinerary.id,
        travelRequestId: itinerary.travelRequestId,
        itineraryTitle: itinerary.title,
        itineraryTotal: itinerary.total,
        userId: itinerary.travelRequest.userId || "",
        customerEmail: itinerary.travelRequest.contact.email,
        agencyStripeAccountId: stripeAccountId,
      });

      await navigator.clipboard.writeText(paymentLink);

      setIsSuccess(true);
      options?.onStateChange?.(false, true);
      setTimeout(() => {
        setIsSuccess(false);
        options?.onStateChange?.(false, false);
      }, 3500);

      toast({
        variant: "info",
        title: t("itinerary.paymentLinkCopied"),
        description: t("itinerary.paymentLinkCopiedDesc"),
      });
    } catch (error) {
      console.error("Error generating payment link:", error);
      options?.onStateChange?.(false, false);
      toast({
        title: t("errors.generic"),
        description: t("itinerary.paymentLinkError"),
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return { generate, isGenerating, isSuccess };
};
